import { faBars, faDollarSign, faHome, faSearch, faStar, faTimes, faUser, faUtensils } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from '@heroui/react';
import { signOut } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { Link, useNavigate } from 'react-router-dom';
import { fetchAllRecipes } from '../api';
import { auth } from '../firebase';
import './header.css';

function Header() {
    const [user, loading] = useAuthState(auth);
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [allRecipes, setAllRecipes] = useState([]);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const navigate = useNavigate();

    // Load all recipes once
    useEffect(() => {
        fetchAllRecipes()
            .then((recipes) => setAllRecipes(recipes || []))
            .catch((err) => console.error('Error fetching recipes:', err));
    }, []);

    const handleLogout = async () => {
        try {
            await signOut(auth);
            window.location = '/';
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    const generateSuggestions = (searchTerm) => {
        if (!searchTerm.trim()) {
            setSuggestions([]);
            return;
        }

        const lowerQuery = searchTerm.toLowerCase();
        const uniqueSuggestions = new Set();

        // Add matching recipe titles
        allRecipes.forEach((recipe) => {
            if (recipe.title.toLowerCase().includes(lowerQuery) && uniqueSuggestions.size < 5) {
                uniqueSuggestions.add({
                    text: recipe.title,
                    type: 'recipe',
                    id: recipe.id,
                });
            }
        });

        // Add matching ingredients
        allRecipes.forEach((recipe) => {
            if (Array.isArray(recipe.ingredients)) {
                recipe.ingredients.forEach((ingredient) => {
                    if (
                        ingredient.toLowerCase().includes(lowerQuery) &&
                        uniqueSuggestions.size < 8
                    ) {
                        uniqueSuggestions.add({
                            text: `Ingredient: ${ingredient}`,
                            type: 'ingredient',
                        });
                    }
                });
            }
        });

        // Add matching cuisines
        allRecipes.forEach((recipe) => {
            if (
                recipe.cuisine &&
                recipe.cuisine.toLowerCase().includes(lowerQuery) &&
                uniqueSuggestions.size < 8
            ) {
                uniqueSuggestions.add({
                    text: `Cuisine: ${recipe.cuisine}`,
                    type: 'cuisine',
                });
            }
        });

        setSuggestions(Array.from(uniqueSuggestions).slice(0, 8));
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
        generateSuggestions(value);
        setShowSuggestions(value.trim().length > 0);
    };

    const handleSuggestionClick = (suggestion) => {
        if (suggestion.type === 'recipe') {
            // Click recipe: go directly to recipe page
            navigate(`/recipe/${suggestion.id}`);
        } else if (suggestion.type === 'ingredient') {
            // Click ingredient: go to recipes with that ingredient
            const ingredientName = suggestion.text.replace('Ingredient: ', '');
            navigate(`/search?q=${encodeURIComponent(ingredientName)}`);
        } else {
            // Click cuisine: go to recipes with that cuisine (keep as is)
            const cuisineName = suggestion.text.replace('Cuisine: ', '');
            navigate(`/search?q=${encodeURIComponent(cuisineName)}`);
        }
        setSearchQuery('');
        setSuggestions([]);
        setShowSuggestions(false);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
            setSearchQuery('');
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };
    return (
        <header className="header">
            {/* Hamburger Menu Button */}
            <button 
                className="hamburger-menu" 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
            >
                <FontAwesomeIcon icon={mobileMenuOpen ? faTimes : faBars} />
            </button>

            <div className="header-logo">
            
                <Link to="/">
                    <img
                        // source from public env
                        src={process.env.PUBLIC_URL + '/DIGITAL-FLAVOR-LOGO.png'}
                        alt="logo" className='logo' />
                </Link>
                
            </div>

            {/* Desktop Navigation */}
            <nav className="desktop-nav">
                <Link to="/" className="nav-link"> Home
                </Link>
                <Link to="/search" className="nav-link">All Recipes
                </Link>
                <Link to="/meals-of-the-day" className="nav-link"> Meals of the Day
                </Link>
                <Link to="/budget-friendly" className="nav-link">Budget-Friendly
                </Link>
            </nav>

            <form onSubmit={handleSearch} className="header-search relative">
                <div className="search-container relative">
                    <input
                        type="text"
                        placeholder="Search recipes..."
                        value={searchQuery}
                        onChange={handleInputChange}
                        onFocus={() => searchQuery.trim() && setShowSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                        className="search-input"
                    />
                    <button type="submit" className="search-button">
                        <FontAwesomeIcon icon={faSearch} />
                    </button>

                    {/* Suggestions Dropdown */}
                    {showSuggestions && suggestions.length > 0 && (
                        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                            {suggestions.map((suggestion, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={() => handleSuggestionClick(suggestion)}
                                    className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 text-sm transition-colors"
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-800">{suggestion.text}</span>
                                        <span className="text-xs text-gray-400 ml-2">
                                            {suggestion.type === 'recipe' ? '🍳' : suggestion.type === 'ingredient' ? '🥕' : '🌍'}
                                        </span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </form>

            <div className="header-right">
                {!loading && user ? (
                    <div className='right-nav-items'>
                       
                        <Dropdown placement="bottom-end">
                            <DropdownTrigger>
                                <div className="user-info center items-center" style={{ cursor: 'pointer' }}>
                                    <img
                                        src={user.photoURL || '/default-user-icon.png'}
                                        alt="User"
                                        className="user-icon"
                                    />
                                    <small>
                                        {user.displayName}
                                    </small>
                                </div>
                            </DropdownTrigger>
                            <DropdownMenu 
                                aria-label="Profile Actions"
                                className="bg-white shadow-2xl rounded-xl p-1 min-w-[200px] border border-gray-100"
                            >
                                <DropdownItem 
                                    key="create" 
                                    onClick={() => { window.location = '../recipes/create' }}
                                    className="font-bold text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 rounded-lg py-2.5 px-3 transition-all mb-2"
                                >
                                    + Create Recipe
                                </DropdownItem>
                                <DropdownItem 
                                    key="profile" 
                                    onClick={() => { window.location = '../user/profile' }}
                                    className="font-semibold text-gray-700 hover:bg-gradient-to-r hover:from-pink-50 hover:to-purple-50 rounded-lg py-2.5 px-3 transition-all"
                                >
                                    My Profile
                                </DropdownItem>
                                <DropdownItem 
                                    key="my-recipes" 
                                    onClick={() => { window.location = '../recipes/myrecipes' }}
                                    className="font-semibold text-gray-700 hover:bg-gradient-to-r hover:from-pink-50 hover:to-purple-50 rounded-lg py-2.5 px-3 transition-all"
                                >
                                    My Recipes
                                </DropdownItem>
                                <DropdownItem 
                                    key="saved" 
                                    onClick={() => { window.location = '../recipes/savedrecipes' }}
                                    className="font-semibold text-gray-700 hover:bg-gradient-to-r hover:from-pink-50 hover:to-purple-50 rounded-lg py-2.5 px-3 transition-all"
                                >
                                    Saved
                                </DropdownItem>
                                <DropdownItem 
                                    key="logout" 
                                    onClick={handleLogout}
                                    className="font-bold text-red-600 hover:bg-red-50 hover:text-red-700 rounded-lg py-2.5 px-3 transition-all mt-1 border-t border-gray-100"
                                >
                                    Logout
                                </DropdownItem>
                            </DropdownMenu>
                        </Dropdown>
                    </div>

                ) : (
                    !loading && (
                        <Link to="/login" className="login-button">
                            Log In
                        </Link>
                    )
                )}
            </div>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
                <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}>
                    <nav className="mobile-menu" onClick={(e) => e.stopPropagation()}>
                        <div className="mobile-menu-header">
                            <h3>Menu</h3>
                            <button onClick={() => setMobileMenuOpen(false)} className="close-menu">
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="mobile-menu-content">
                            <Link to="/" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                                <FontAwesomeIcon icon={faHome} />
                                <span>Home</span>
                            </Link>
                            <Link to="/recipes/all" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                                <FontAwesomeIcon icon={faUtensils} />
                                <span>All Recipes</span>
                            </Link>
                            <Link to="/meals-of-the-day" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                                <FontAwesomeIcon icon={faStar} />
                                <span>Meals of the Day</span>
                            </Link>
                            <Link to="/budget-friendly" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                                <FontAwesomeIcon icon={faDollarSign} />
                                <span>Budget-Friendly</span>
                            </Link>
                            {user ? (
                                <>
                                    <div className="mobile-menu-divider"></div>
                                    <Link to="/recipes/myrecipes" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                                        <FontAwesomeIcon icon={faUtensils} />
                                        <span>My Recipes</span>
                                    </Link>
                                    <Link to="/recipes/savedrecipes" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                                        <span>❤️ Saved Recipes</span>
                                    </Link>
                                    <Link to="/user/profile" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                                        <FontAwesomeIcon icon={faUser} />
                                        <span>My Profile</span>
                                    </Link>
                                    <div className="mobile-menu-divider"></div>
                                    <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="mobile-nav-link logout">
                                        <span>Logout</span>
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="mobile-menu-divider"></div>
                                    <Link to="/login" className="mobile-nav-link highlight" onClick={() => setMobileMenuOpen(false)}>
                                        <span>Log In / Sign Up</span>
                                    </Link>
                                </>
                            )}
                        </div>
                    </nav>
                </div>
            )}

        </header>
    );
}

export default Header;
